import type { Book } from '@/types/domain'
import { getSupabase, EPUB_BUCKET, COVERS_BUCKET } from './supabase'
import { useAuthStore } from './auth'
import { useSettingsStore } from '@/stores/settings'
import { upsertCloudBook, deleteCloudBook } from './cloud-index'
import * as repo from '@/db/repositories'

/**
 * Límite de tamaño por archivo EPUB en la nube. Coincide con el tope por
 * defecto de Supabase Storage (50 MB en Free tier). Si tu plan de Supabase
 * lo sube, ajusta este valor para que el check client-side no rechace
 * archivos válidos. El servidor rechazará igual todo lo que pase del tope
 * real del proyecto.
 */
export const MAX_EPUB_SIZE_BYTES = 50 * 1024 * 1024 // 50 MB

/**
 * Error tipado para archivos que exceden el límite de tamaño. Permite a la
 * UI mostrar un mensaje claro con el tamaño real en vez del error crudo
 * del servidor.
 */
export class EpubTooLargeError extends Error {
  /** Tamaño real del archivo en bytes. */
  readonly actualBytes: number
  /** Límite aplicado en bytes. */
  readonly limitBytes: number
  constructor(actualBytes: number, limitBytes = MAX_EPUB_SIZE_BYTES) {
    super(
      `El archivo pesa ${(actualBytes / 1024 / 1024).toFixed(1)} MB; el límite es ${(limitBytes / 1024 / 1024).toFixed(0)} MB.`,
    )
    this.name = 'EpubTooLargeError'
    this.actualBytes = actualBytes
    this.limitBytes = limitBytes
  }
}

/**
 * Subida y bajada de archivos EPUB al Storage privado de Supabase.
 *
 * La ruta de cada archivo es `<userId>/<bookId>.epub`, de modo que la
 * política RLS (que valida el primer segmento del path contra el
 * auth.uid()) aísle los libros de cada usuario.
 *
 * Al subir, también se indexa el libro en `cloud_books` para que otros
 * dispositivos puedan descubrirlo y descargarlo.
 */

/**
 * Sube el EPUB a Storage y lo indexa en cloud_books.
 * Recibe el Book completo (necesita metadatos para el índice).
 */
export async function uploadEpub(book: Book): Promise<void> {
  await uploadEpubBlob(book)
  // Indexar en la nube para que otros dispositivos lo descubran.
  await upsertCloudBook(book)
}

/**
 * Sube SOLO el blob del EPUB al storage (sin tocar el índice `cloud_books`).
 * Útil para subidas bulk: subes N blobs y luego indexas en una sola
 * petición con `upsertCloudBooks`.
 */
export async function uploadEpubBlob(book: Book): Promise<void> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) throw new Error('No hay sesión para subir el archivo.')

  // v18: el binario ya no vive inline en `book`; cargarlo bajo demanda.
  const blob = book.fileBlob ?? (await repo.getBookFile(book.id))
  if (!blob) throw new Error(`No se encontró el archivo de "${book.title}".`)

  // Check client-side de tamaño: evita el error crudo del servidor y da
  // un mensaje claro con el tamaño real. El servidor volverá a validar.
  if (blob.size > MAX_EPUB_SIZE_BYTES) {
    throw new EpubTooLargeError(blob.size)
  }

  const path = `${auth.userId}/${book.id}.epub`
  const { error } = await client.storage
    .from(EPUB_BUCKET)
    .upload(path, blob, {
      contentType: 'application/epub+zip',
      upsert: true,
    })
  if (error) throw new Error(`Error al subir: ${error.message}`)

  // Subir también la portada (mejor-esfuerzo). Si no hay portada o falla,
  // no rompemos la subida del epub: el libro queda disponible aunque la
  // sección "En la nube" muestre el fallback editorial para ese título.
  try {
    const cover = book.coverBlob ?? (await repo.getBookCover(book.id))
    if (cover) await uploadCover(book.id, cover)
  } catch (e) {
    console.warn('[storage] subida de portada falló (ignorada):', e)
  }
}

/**
 * Sube la portada de un libro al bucket `covers`. La ruta es
 * `<userId>/<bookId>` (sin extensión); el content-type se deriva del blob
 * si declara un tipo imagen válido, si no se asume `image/jpeg` (las
 * portadas reales suelen ser JPEG). `upsert: true` para reemplazar.
 */
export async function uploadCover(bookId: string, blob: Blob): Promise<void> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) throw new Error('No hay sesión para subir la portada.')

  const contentType = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg'
  const path = `${auth.userId}/${bookId}`
  const { error } = await client.storage
    .from(COVERS_BUCKET)
    .upload(path, blob, { contentType, upsert: true })
  if (error) throw new Error(`Error al subir la portada: ${error.message}`)
}

/**
 * Devuelve una URL firmada (válido 1h) para la portada de un libro en la
 * nube, o `null` si el libro no tiene portada subida. El caller debe
 * degradar a su fallback cuando reciba `null`.
 *
 * Verificamos la existencia con `list()` (patrón de `isEpubUploaded`) en
 * vez de con un HEAD sobre la signed URL: Supabase Storage no responde de
 * forma fiable a HEAD sobre buckets privados con signed URLs (a veces 400
 * o cuelga), lo que hacía que TODAS las portadas cayeran al fallback.
 */
/**
 * ¿Existe la portada de `bookId` en el bucket `covers`? Verificación vía
 * `list()` (respeta RLS), con coincidencia exacta de nombre para no
 * confundir prefijos. Reutilizable por el rescáner client-side.
 */
export async function coverExists(bookId: string): Promise<boolean> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) return false
  const { data, error } = await client.storage
    .from(COVERS_BUCKET)
    .list(auth.userId, { search: bookId })
  if (error) return false
  return (data ?? []).some((f) => f.name === bookId)
}

/**
 * Devuelve una URL firmada (válido 1h) para la portada de un libro en la
 * nube, o `null` si el libro no tiene portada subida. El caller debe
 * degradar a su fallback cuando reciba `null`.
 *
 * Verificamos la existencia con `coverExists()` (list) en vez de con un
 * HEAD sobre la signed URL: Supabase Storage no responde de forma fiable
 * a HEAD sobre buckets privados con signed URLs (a veces 400 o cuelga),
 * lo que hacía que TODAS las portadas cayeran al fallback.
 */
export async function getCoverSignedUrl(bookId: string): Promise<string | null> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) return null

  if (!(await coverExists(bookId))) return null

  const path = `${auth.userId}/${bookId}`
  const { data, error } = await client.storage
    .from(COVERS_BUCKET)
    .createSignedUrl(path, 3600)
  if (error || !data?.signedUrl) return null
  return data.signedUrl
}

/**
 * Descarga el EPUB de un libro como Blob. Lanza si no existe o falla.
 * Usa una URL firmada (bucket privado) y luego fetch.
 */
export async function downloadEpub(bookId: string): Promise<Blob> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) throw new Error('No hay sesión para descargar el archivo.')

  const path = `${auth.userId}/${bookId}.epub`
  const { data, error } = await client.storage
    .from(EPUB_BUCKET)
    .createSignedUrl(path, 3600) // 1 hora de validez
  if (error || !data?.signedUrl) {
    throw new Error(`Error al descargar: ${error?.message ?? 'URL no disponible'}`)
  }

  const res = await fetch(data.signedUrl)
  if (!res.ok) throw new Error(`Descarga fallida: ${res.status}`)
  return res.blob()
}

/** Comprueba si un libro ya está subido a la nube. */
export async function isEpubUploaded(bookId: string): Promise<boolean> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) return false

  const { data, error } = await client.storage.from(EPUB_BUCKET).list(auth.userId, {
    search: `${bookId}.epub`,
  })
  if (error) return false
  return (data ?? []).some((f) => f.name === `${bookId}.epub`)
}

/** Elimina el EPUB de Storage y lo quita del índice cloud_books. */
export async function deleteEpub(bookId: string): Promise<void> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) return

  const path = `${auth.userId}/${bookId}.epub`
  const { error } = await client.storage.from(EPUB_BUCKET).remove([path])
  if (error) throw new Error(`Error al eliminar: ${error.message}`)

  // Borrar también la portada del bucket `covers` (mejor-esfuerzo: si no
  // existe o falla, no rompemos el borrado del libro).
  try {
    await client.storage.from(COVERS_BUCKET).remove([`${auth.userId}/${bookId}`])
  } catch (e) {
    console.warn('[storage] borrado de portada falló (ignorado):', e)
  }

  // Quitar del índice de la nube.
  await deleteCloudBook(bookId)
}

/**
 * Elimina VARIOS EPUBs de Storage en una sola llamada `.remove([paths])`
 * y luego los quita del índice `cloud_books` con una query `.in('id')`.
 * Mucho más eficiente que N `deleteEpub` individuales.
 *
 * Devuelve el número de libros que NO se pudieron borrar (0 si todo OK).
 * Los errores parciales se registran en consola pero no lanzan.
 */
export async function deleteEpubs(bookIds: string[]): Promise<{ failed: number }> {
  if (bookIds.length === 0) return { failed: 0 }
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) return { failed: bookIds.length }

  const paths = bookIds.map((id) => `${auth.userId}/${id}.epub`)
  const { error } = await client.storage.from(EPUB_BUCKET).remove(paths)
  if (error) {
    console.error('[storage] remove falló:', error.message)
    // No sabemos cuáles fallaron; el caller puede recargar para ver el estado.
    return { failed: bookIds.length }
  }

  // Borrar también las portadas del bucket `covers` en una sola llamada
  // (mejor-esfuerzo: ignoramos errores parciales).
  try {
    const coverPaths = bookIds.map((id) => `${auth.userId}/${id}`)
    const { error: coverError } = await client.storage.from(COVERS_BUCKET).remove(coverPaths)
    if (coverError) console.warn('[storage] remove covers falló (ignorado):', coverError.message)
  } catch (e) {
    console.warn('[storage] remove covers falló (ignorado):', e)
  }

  // Quitar del índice en una sola query.
  try {
    const { deleteCloudBooks } = await import('./cloud-index')
    await deleteCloudBooks(bookIds)
  } catch (e) {
    console.error('[storage] deleteCloudBooks falló:', e)
    return { failed: bookIds.length }
  }
  return { failed: 0 }
}

// ---------- EPUBs compartidos (Área F) ----------
//
// Para compartir el archivo con los miembros de un share, el owner publica
// una copia en la ruta `shared/<shareId>/<bookId>.epub`. La policy RLS de
// Storage permite la LECTURA a miembros/owner y la SUBIDA solo al owner.
// Así los miembros pueden descargar el libro sin ver la carpeta privada
// del owner.

/** Sube (o reemplaza) la copia compartida del EPUB de un libro. Solo owner. */
export async function uploadSharedEpub(shareId: string, bookId: string, blob: Blob): Promise<void> {
  const client = requireClient()
  const path = `shared/${shareId}/${bookId}.epub`
  const { error } = await client.storage
    .from(EPUB_BUCKET)
    .upload(path, blob, { contentType: 'application/epub+zip', upsert: true })
  if (error) throw new Error(`Error al subir compartido: ${error.message}`)
}

/** Descarga el EPUB compartido de un libro como Blob (miembro u owner). */
export async function downloadSharedEpub(shareId: string, bookId: string): Promise<Blob> {
  const client = requireClient()
  const path = `shared/${shareId}/${bookId}.epub`
  const { data, error } = await client.storage
    .from(EPUB_BUCKET)
    .createSignedUrl(path, 3600)
  if (error || !data?.signedUrl) {
    throw new Error(`Error al descargar compartido: ${error?.message ?? 'URL no disponible'}`)
  }
  const res = await fetch(data.signedUrl)
  if (!res.ok) throw new Error(`Descarga compartida fallida: ${res.status}`)
  return res.blob()
}

/** Obtiene el cliente Supabase o lanza si no hay configuración. */
function requireClient() {
  const settings = useSettingsStore()
  const client = getSupabase(settings.settings.sync)
  if (!client) throw new Error('Supabase no configurado. Activa el sync en Ajustes.')
  return client
}
